import { User } from 'lucide-react';
import { round } from '@/utils/mathUtils';
import { useState, useCallback, useEffect, useMemo, memo } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { SaveButton } from '@/components/ui/save-button';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/context/AuthContext';
import { useSavePurchaseRequest } from '@/hooks/useSavePurchaseRequest';

import type {
  IItemData,
  IPurchaseRequestItemBody,
  ISuppliarWithDebt,
  SaveRequestDto,
  SaveRequestItemDto,
  IRequestType,
  ISuppliars,
  UpdateRequestDto,
} from '../../api/HandRequest.interface';
import HeaderRequest from '../Header';

import ConfirmCancelDialog from './components/ConfirmCancelDialog';
import ConfirmDialog from './components/ConfirmDialog';
import LoadingOverlay from './components/LoadingOverlay';
import RequestFooter from './components/RequestFooter';
import RequestInputs from './components/RequestInputs';
import RequestItems from './components/RequestItems';
import { RequestSuccessDialog } from './components/RequestSuccessDialog';

// Create MemoizedHeaderRequest
const MemoizedHeaderRequest = memo(HeaderRequest);

// Create a custom cancel button component with confirmation
const CancelButton = memo(({ onCancel }: { onCancel: () => void }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="destructive"
          className="h-8 whitespace-nowrap px-3 shadow-sm transition-all hover:shadow"
        >
          الغاء
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4">
        <div className="flex flex-col gap-3">
          <p className="text-right text-sm font-medium" dir="rtl">
            هل تريد الإغلاق؟
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                onCancel();
                setIsOpen(false);
              }}
            >
              نعم
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsOpen(false)}
            >
              لا
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
});

CancelButton.displayName = 'CancelButton';

type Props = {
  items: IItemData[] | undefined;
  suppliar: ISuppliarWithDebt | null;
  requestType?: IRequestType;
  handleReset?: () => void;
  handleSelectedSuppliar?: (value: ISuppliars | null) => Promise<void>;
  suppliers?: ISuppliars[];
  isEditMode?: boolean;
  itemsInRequest?: IPurchaseRequestItemBody[];
  requestNotes?: string;
  requestID?: string;
};

function StoreRequest({
  items,
  suppliar,
  requestType,
  handleReset,
  handleSelectedSuppliar,
  suppliers,
  isEditMode = false,
  requestNotes = '',
  itemsInRequest = [],
  requestID,
}: Props) {
  // Core state with performance optimizations
  const [requestItems, setRequestItems] = useState<IPurchaseRequestItemBody[]>(
    isEditMode ? itemsInRequest : [],
  );
  const [selectedUpdateItem, setSelectedUpdateItem] =
    useState<IPurchaseRequestItemBody | null>(null);
  const [notes, setNotes] = useState<string>(isEditMode ? requestNotes : '');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [loadingStart, setLoadingStart] = useState<number | null>(null);

  // Dialog states
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successData, setSuccessData] = useState<{
    requestId: string;
    total: number;
    itemsCount: number;
  } | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [itemsRequiringConfirmation, setItemsRequiringConfirmation] = useState<
    { item: IPurchaseRequestItemBody; reasons: string[] }[]
  >([]);

  // API hook
  const { createRequest, updateRequest, isSubmitting, isUpdating, isCreating } =
    useSavePurchaseRequest();

  // Memoized user data to prevent unnecessary re-renders
  const { user: userData } = useAuth();
  const user = useMemo(
    () => ({
      name: userData?.empName,
      avatar: userData?.avatar
        ? `${import.meta.env.VITE_HEALTHY_EMPIRE_SERVER}/public/avatar/thumb/${userData?.avatar}`
        : '/images/no-user.jpg',
      jobTitle: userData?.job?.jobName,
    }),
    [userData],
  );

  // Create a function to handle the reset action
  const handleResetWithConfirmation = useCallback(() => {
    if (requestItems.length > 0) {
      setShowCancelDialog(true);
    } else if (handleReset) {
      handleReset();
    }
  }, [handleReset, requestItems.length]);

  // Function to perform the actual reset
  const performReset = useCallback(() => {
    if (handleReset) {
      handleReset();
      setRequestItems([]);
      setNotes('');
      setSelectedUpdateItem(null);
      toast({
        description: 'تم إلغاء الطلب',
      });
    }
  }, [handleReset]);

  // Render HeaderRequest if props are provided
  const renderHeaderRequest = useMemo(() => {
    if (handleReset && requestType && handleSelectedSuppliar && suppliers) {
      return (
        <MemoizedHeaderRequest
          handleReset={handleResetWithConfirmation}
          requestType={requestType}
          selectedSuppliar={suppliar}
          handleSelectedSuppliar={handleSelectedSuppliar}
          suppliars={suppliers}
          isEditMode={isEditMode}
          requestID={requestID}
        />
      );
    }
    return null;
  }, [
    handleReset,
    requestType,
    handleSelectedSuppliar,
    suppliers,
    handleResetWithConfirmation,
    suppliar,
    isEditMode,
    requestID,
  ]);

  // Memoized calculations
  const calculateTotalAmount = useCallback(
    () =>
      requestItems.reduce(
        (sum, item) => sum + (item.TotalAfterDiscount || 0),
        0,
      ),
    [requestItems],
  );

  // Optimized: timing utility for minimum loading display
  const ensureMinimumLoadTime = useCallback(
    (callback: () => void) => {
      if (!loadingStart) {
        setIsSaving(false);
        return callback();
      }

      const elapsedTime = Date.now() - loadingStart;
      const minimumTime = 4000;

      if (elapsedTime >= minimumTime) {
        setIsSaving(false);
        setLoadingStart(null);
        callback();
      } else {
        setTimeout(() => {
          setIsSaving(false);
          setLoadingStart(null);
          callback();
        }, minimumTime - elapsedTime);
      }
    },
    [loadingStart],
  );

  // Memoized validation function to prevent recreation
  const validateRequestData = useCallback((data: SaveRequestDto): boolean => {
    try {
      const errorMessages: string[] = [];
      let hasErrors = false;

      // Essential validations
      if (!data.clientID) {
        errorMessages.push('معرف المورد غير صحيح');
        hasErrors = true;
      }

      if (!data.items?.length) {
        errorMessages.push('يجب إضافة صنف واحد على الأقل');
        hasErrors = true;
      } else {
        // Fast validation loop - only check critical fields
        for (let i = 0; i < data.items.length; i++) {
          const item = data.items[i];
          const index = i + 1;

          if (!item.HealthyItemID) {
            errorMessages.push(`معرف الصنف #${index} غير صحيح`);
            hasErrors = true;
          }
          if (!item.Qty || item.Qty < 1) {
            errorMessages.push(`كمية الصنف #${index} يجب أن تكون أكبر من صفر`);
            hasErrors = true;
          }
          if (!item.BNO) {
            errorMessages.push(`رقم التشغيلة للصنف #${index} مطلوب`);
            hasErrors = true;
          }
          if (!item.ExpireDate) {
            errorMessages.push(`تاريخ انتهاء الصنف #${index} مطلوب`);
            hasErrors = true;
          }
        }
      }

      if (hasErrors) {
        toast({
          title: 'خطأ في البيانات',
          description: errorMessages.join('\n'),
          variant: 'destructive',
        });
      }

      return hasErrors;
    } catch (error) {
      toast({
        title: 'خطأ في التحقق من البيانات',
        description: 'حدث خطأ أثناء التحقق من صحة البيانات',
        variant: 'destructive',
      });
      return true;
    }
  }, []);

  // Item verification before save (memoized)
  const checkItemsBeforeSave = useCallback(() => {
    const needConfirmation: {
      item: IPurchaseRequestItemBody;
      reasons: string[];
    }[] = [];

    for (const item of requestItems) {
      const reasons: string[] = [];

      // Fast verification checks
      if (item.DiscountStatus === 'bad' || item.Discount <= 10) {
        reasons.push('خصم غير مناسب (أقل من المطلوب) 😕');
      }
      if (item?.UnderRequest && item?.UnderRequest > 0) {
        reasons.push('هذا الصنف عليه طلبات معلقة 🔄 ⏳ 🚫');
      }
      if (item.WholeSaleStatus === 'overStock') {
        reasons.push('فائض مخزون في الجملة 📦📦📦');
      }
      if (item.RetailStatus === 'overStock') {
        reasons.push('فائض مخزون في التجزئة 📦📦📦');
      }
      if (item.WholeSaleStatus === 'canTransfer') {
        reasons.push('يمكن نقله من فرع آخر (الجملة) 🚚');
      }
      if (item.RetailStatus === 'canTransfer') {
        reasons.push('يمكن نقله من فرع آخر (التجزئة) 🚚');
      }

      if (reasons.length > 0) {
        needConfirmation.push({ item, reasons });
      }
    }

    return needConfirmation;
  }, [requestItems]);

  // Optimized save request function
  const saveRequest = useCallback(() => {
    setIsSaving(true);
    setLoadingStart(Date.now());

    // Create payload with fast mapping
    const requestData: SaveRequestDto = {
      clientID: suppliar!.oid,
      notes: notes.trim() || undefined,
      items: requestItems.map(
        (item): SaveRequestItemDto => ({
          HealthyItemID: item.Item_ID,
          Qty: item.Qty,
          Bonus: item.Bonus || 0,
          Price: round(item.Price, 2),
          Discount: round(item.Discount, 2),
          DiscountPro: round(item.DiscountPro, 2),
          Tax: round(item.Tax, 2),
          BNO: item.BNO,
          ExpireDate: item.ExpireDate,
          DiscountStatus: item.DiscountStatus,
          WholeSaleStatus: item.WholeSaleStatus,
          RetailStatus: item.RetailStatus,
          UnderRequest: item.UnderRequest || 0,
        }),
      ),
    };

    // Validate essential data before API call
    if (validateRequestData(requestData)) {
      setIsSaving(false);
      setLoadingStart(null);
      return;
    }

    // For edit mode, use updateRequest with the request ID
    if (isEditMode && requestID) {
      const updateData: UpdateRequestDto = {
        ...requestData,
        headID: requestID,
      };

      // API call with proper error handling for update
      updateRequest(updateData)
        .then((response) => {
          if (!response) {
            setIsSaving(false);
            setLoadingStart(null);
            return;
          }

          if (response.success) {
            setSuccessData({
              requestId: response.requestId || requestID,
              total: round(response.total || calculateTotalAmount(), 2),
              itemsCount: response.itemsCount || requestItems.length,
            });

            ensureMinimumLoadTime(() => setShowSuccessDialog(true));
          } else {
            ensureMinimumLoadTime(() => {
              toast({
                title: 'خطأ في التعديل',
                description: response.message,
                variant: 'destructive',
              });
            });
          }
        })
        .catch(() => {
          ensureMinimumLoadTime(() => {
            toast({
              title: 'خطأ في التعديل',
              description: 'حدث خطأ أثناء تعديل الطلب. يرجى المحاولة مرة أخرى.',
              variant: 'destructive',
            });
          });
        });
    } else {
      // API call with proper error handling for create
      createRequest(requestData)
        .then((response) => {
          if (!response) {
            setIsSaving(false);
            setLoadingStart(null);
            return;
          }

          if (response.success) {
            setSuccessData({
              requestId: response.requestId || 'UNKNOWN_ID',
              total: round(response.total || calculateTotalAmount(), 2),
              itemsCount: response.itemsCount || requestItems.length,
            });

            ensureMinimumLoadTime(() => setShowSuccessDialog(true));
          } else {
            ensureMinimumLoadTime(() => {
              toast({
                title: 'خطأ في الحفظ',
                description: response.message,
                variant: 'destructive',
              });
            });
          }
        })
        .catch(() => {
          ensureMinimumLoadTime(() => {
            toast({
              title: 'خطأ في الحفظ',
              description: 'حدث خطأ أثناء حفظ الطلب. يرجى المحاولة مرة أخرى.',
              variant: 'destructive',
            });
          });
        });
    }
  }, [
    requestItems,
    suppliar,
    notes,
    calculateTotalAmount,
    ensureMinimumLoadTime,
    createRequest,
    updateRequest,
    validateRequestData,
    isEditMode,
    requestID,
  ]);

  // Optimized save handler with early validations
  const handleSave = useCallback(() => {
    if (!suppliar) {
      toast({
        title: 'خطأ',
        description: 'يجب اختيار المورد قبل حفظ الطلب',
        variant: 'destructive',
      });
      return;
    }

    if (requestItems.length === 0) {
      toast({
        title: 'خطأ',
        description: 'يجب إضافة صنف واحد على الأقل للطلب',
        variant: 'destructive',
      });
      return;
    }

    const itemsToConfirm = checkItemsBeforeSave();
    if (itemsToConfirm.length > 0) {
      setItemsRequiringConfirmation(itemsToConfirm);
      setShowConfirmDialog(true);
    } else {
      saveRequest();
    }
  }, [requestItems, suppliar, checkItemsBeforeSave, saveRequest]);

  // Optimized CRUD operations for request items
  const updateRequestItem = useCallback(
    (newItem: IPurchaseRequestItemBody): boolean => {
      const itemIndex = requestItems.findIndex(
        (item) =>
          (newItem._id !== null && item._id === newItem._id) ||
          item.TempID === newItem.TempID,
      );

      if (itemIndex === -1) return false;

      setRequestItems((prev) => {
        const newItems = [...prev];
        newItems[itemIndex] = newItem;
        return newItems;
      });

      setSelectedUpdateItem(null);
      return true;
    },
    [requestItems],
  );

  const addRequestItem = useCallback(
    (newItem: IPurchaseRequestItemBody): boolean => {
      // Check for duplicates using Set for improved performance
      const existingIds = new Set(
        requestItems
          .map((item) => (item._id !== null ? item._id : item.TempID))
          .filter(Boolean),
      );

      if (
        (newItem._id !== null && existingIds.has(newItem._id)) ||
        existingIds.has(newItem.TempID)
      ) {
        return false;
      }

      setRequestItems((prev) => [newItem, ...prev]);
      return true;
    },
    [requestItems],
  );

  const deleteRequestItem = useCallback(
    (item: IPurchaseRequestItemBody) => {
      setRequestItems((prev) => prev.filter((i) => i !== item));

      if (selectedUpdateItem?.TempID === item.TempID) {
        setSelectedUpdateItem(null);
      }
    },
    [selectedUpdateItem],
  );

  // Simple state setters (memoized)
  const handleSelectItemForUpdate = useCallback(
    (item: IPurchaseRequestItemBody) => setSelectedUpdateItem(item),
    [],
  );

  const handleNewRequest = useCallback(() => {
    setShowSuccessDialog(false);
    setRequestItems([]);
    setNotes('');
    setSelectedUpdateItem(null);
    toast({
      description: 'تم تهيئة الطلب لإنشاء طلب جديد',
    });
  }, []);

  // Keyboard shortcut handling
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Key code 90 is for 'Z' regardless of language/keyboard layout
      if (event.ctrlKey && event.shiftKey && event.keyCode === 90) {
        event.preventDefault();
        if (requestItems.length > 0 && !isSaving && suppliar) {
          handleSave();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [requestItems, isSaving, suppliar, handleSave]);

  // Memoized condition checks for UI rendering
  const canSave = useMemo(
    () => requestItems.length > 0 && !isSubmitting && !!suppliar,
    [requestItems.length, isSubmitting, suppliar],
  );

  const saveButtonText = useMemo(
    () => (isEditMode ? 'تعديل الطلب' : 'حفظ الطلب'),
    [isEditMode],
  );
  const isLoading = useMemo(
    () => (isEditMode ? isUpdating : isCreating),
    [isEditMode, isUpdating, isCreating],
  );
  const loadingText = useMemo(
    () => (isEditMode ? 'جاري التعديل...' : 'جاري الحفظ...'),
    [isEditMode],
  );

  // Optimized render with minimal calculations
  return (
    <div>
      {renderHeaderRequest}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-[500px_1fr]">
        <RequestInputs
          items={items}
          suppliar={suppliar}
          addRequestItem={addRequestItem}
          updateRequestItem={updateRequestItem}
          requestItems={requestItems}
          selectedUpdateItem={selectedUpdateItem}
        />

        <div className="flex flex-col">
          <div className="overflow-auto rounded-lg">
            <RequestItems
              requestItems={requestItems}
              deleteRequestItem={deleteRequestItem}
              selectItemForUpdate={handleSelectItemForUpdate}
              selectedUpdateItem={selectedUpdateItem}
            />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 rounded-xl border bg-gradient-to-b from-white to-slate-50 p-4 shadow-sm dark:border-slate-800 dark:from-slate-900 dark:to-slate-950">
            <div className="grid grid-cols-1 gap-y-3 md:grid-cols-[1fr_auto] md:gap-x-4">
              <div className="flex flex-col gap-3">
                <RequestFooter
                  requestItems={requestItems}
                  suppliarWithDebt={suppliar}
                  notes={notes}
                  setNotes={setNotes}
                />
              </div>

              <div className="flex flex-col justify-between gap-3 md:w-48">
                <div className="flex flex-col gap-2">
                  <div className="group relative overflow-hidden rounded-xl border border-slate-200/50 bg-gradient-to-br from-slate-50 to-white p-3 shadow-md transition-all duration-300 hover:shadow-lg dark:border-slate-700/50 dark:from-slate-800 dark:to-slate-900/90">
                    <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_0%_0%,rgba(56,189,248,0.03),transparent_50%)]"></div>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-11 w-11 border-2 border-white/80 shadow-sm transition-transform duration-300 group-hover:scale-110 dark:border-slate-700">
                        <AvatarImage
                          src={user.avatar}
                          alt={user.name}
                          className="object-cover"
                        />
                        <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-500 text-white">
                          <User className="h-6 w-6" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="text-sm">
                        <div className="font-medium text-slate-900 dark:text-slate-100">
                          {user.name || 'المستخدم الحالي'}
                        </div>
                        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                          {user.jobTitle || 'الصيدلية'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex w-full flex-row-reverse gap-2 py-4 md:py-0">
                  <SaveButton
                    size="sm"
                    className={`group relative h-16 overflow-hidden rounded-xl ${
                      isEditMode
                        ? 'bg-gradient-to-br from-amber-600 via-amber-500 to-amber-600 hover:from-amber-500 hover:via-amber-400 hover:to-amber-500 hover:shadow-amber-500/25 dark:hover:shadow-amber-600/20'
                        : 'bg-gradient-to-br from-green-600 via-green-500 to-emerald-600 hover:from-green-500 hover:via-green-400 hover:to-emerald-500 hover:shadow-green-500/25 dark:hover:shadow-green-600/20'
                    } px-5 text-white shadow-lg transition-all duration-300 hover:scale-[1.02] dark:from-green-600 dark:via-green-500 dark:to-emerald-600`}
                    disabled={!canSave}
                    isLoading={isLoading}
                    loadingText={loadingText}
                    onClick={handleSave}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_50%_120%,rgba(120,255,215,0.1),transparent_70%)]"></div>
                      <span className="text-base font-medium tracking-wide">
                        {saveButtonText}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <kbd className="rounded-md border border-white/30 bg-white/20 px-2 py-0.5 text-[10px] font-medium text-white shadow-sm backdrop-blur-sm">
                          Ctrl
                        </kbd>
                        <span className="text-white/80">+</span>
                        <kbd className="rounded-md border border-white/30 bg-white/20 px-2 py-0.5 text-[10px] font-medium text-white shadow-sm backdrop-blur-sm">
                          Shift
                        </kbd>
                        <span className="text-white/80">+</span>
                        <kbd className="rounded-md border border-white/30 bg-white/20 px-2 py-0.5 text-[10px] font-medium text-white shadow-sm backdrop-blur-sm">
                          Z
                        </kbd>
                      </div>
                    </div>
                  </SaveButton>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Conditionally rendered dialogs with minimal props */}
        {showSuccessDialog && successData && (
          <RequestSuccessDialog
            isOpen={true}
            onNewRequest={handleNewRequest}
            requestId={successData.requestId}
            total={successData.total}
            itemsCount={successData.itemsCount}
            supplierName={suppliar?.ClientName || ''}
            isEditMode={isEditMode}
            handleReset={handleReset}
          />
        )}

        <ConfirmDialog
          isOpen={showConfirmDialog}
          onOpenChange={setShowConfirmDialog}
          itemsRequiringConfirmation={itemsRequiringConfirmation}
          onConfirm={() => {
            setShowConfirmDialog(false);
            saveRequest();
          }}
          onCancel={() => setShowConfirmDialog(false)}
        />

        <ConfirmCancelDialog
          isOpen={showCancelDialog}
          onOpenChange={setShowCancelDialog}
          onConfirm={performReset}
        />

        <LoadingOverlay isActive={isSaving} />
      </div>
    </div>
  );
}

export default StoreRequest;
